/**
 * "Your checkpoints don't change scores yet" — the client half of owner
 * decision D-A (b), 2026-10-09.
 *
 * A coach can add checkpoints to a template and then set Scoring Emphasis to
 * body form only; the checkpoints then carry zero weight and silently do
 * nothing. The owner chose a WARNING over a default change. The backend emits
 * it in the finalize validation report; this module is the same condition,
 * client-side, so the coach sees it at the slider rather than at finalize.
 *
 * THE CONDITION MUST MATCH THE BACKEND EXACTLY.
 * Backend: `template_validation._check_checkpoints_carry_no_weight`. All four
 * clauses, and why each one is load-bearing:
 *
 * 1. `bodyFormEnabled` — `metadata.global_body_match_enabled`. With body-form
 *    scoring off the pipeline never reaches the blend that reads the weight, so
 *    a stored 1.0 is inert.
 * 2. `bodyFormWeight >= 1` — `metadata.global_body_match_weight`, the same
 *    `>= 1.0` comparison the pipeline uses. `phase_weighted_scoring` blends
 *    `w_goals = (1 - weight) * w_rest`, so at 1.0 every checkpoint score is
 *    multiplied by zero.
 * 3. At least one checkpoint — an annotation carrying one of the seven goal
 *    carriers the scoring loop reads (`GOAL_CARRIER_FIELDS` below mirrors
 *    `template_validation._GOAL_CARRIER_FIELDS`). With no checks there is
 *    nothing being silently ignored.
 * 4. At least one BODY REGION annotation — mirrors
 *    `phase_weighted_scoring.template_has_body_region_annotations`. When a
 *    template has none, the pipeline's goal-only auto-route forces the
 *    effective body-form weight to 0.0 and the checkpoints carry FULL weight
 *    whatever the slider says, so the warning would be false.
 *
 * Deliberately NOT a component: it is a predicate, and `react-refresh/
 * only-export-components` forbids exporting one from a component module (the
 * same reason `scoringPresets.ts` is its own file).
 */

/**
 * The seven goal-carrier field names the scoring loop recognises.
 *
 * Mirrors `backend/app/services/template_validation.py::_GOAL_CARRIER_FIELDS`,
 * which in turn mirrors `evaluation_pipeline.prepare_annotations`'s filter.
 * `signed_projection_goal` / `height_comparison_goal` are the rule compiler's
 * two detector carriers: they are not declared on the canvas `Annotation`
 * interface, but a materialised suggestion the coach accepted carries them at
 * runtime — which is why this module reads annotations structurally rather than
 * through that type.
 */
export const GOAL_CARRIER_FIELDS = [
  'spatial_goal',
  'movement_goal',
  'timing_goal',
  'axis_goal',
  'joint_angle_goal',
  'signed_projection_goal',
  'height_comparison_goal',
] as const;

function asRecord(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === 'object'
    ? (value as Record<string, unknown>)
    : null;
}

/**
 * Python's truthiness, not JavaScript's.
 *
 * Both backend clauses are plain truthiness tests (`ann.get('axis_goal')`,
 * `a.get('spatial_goal')`), and in Python an EMPTY dict and an EMPTY list are
 * falsy while in JavaScript `{}` and `[]` are truthy. Without this, an
 * annotation carrying `axis_goal: {}` would be a checkpoint on the client and
 * not on the server — the two sides would disagree about whether to warn. Found
 * by diffing both halves of the condition (CLAUDE.md step 10), not in review.
 */
function truthyAsPython(value: unknown): boolean {
  if (value === null || value === undefined || value === false) return false;
  if (typeof value === 'number') return value !== 0;
  if (typeof value === 'string') return value.length > 0;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'object') return Object.keys(value).length > 0;
  return Boolean(value);
}

/** True when this annotation carries a goal the scoring loop reads. */
export function isCheckpoint(annotation: unknown): boolean {
  const record = asRecord(annotation);
  if (!record) return false;
  return GOAL_CARRIER_FIELDS.some((field) => truthyAsPython(record[field]));
}

/** How many of these annotations are checkpoints. */
export function countCheckpoints(annotations: readonly unknown[]): number {
  return annotations.filter(isCheckpoint).length;
}

/**
 * True when at least one annotation defines body-region coverage.
 *
 * Mirrors `phase_weighted_scoring.template_has_body_region_annotations`:
 * `landmark_indices` (a region that actually contributes body-form matching)
 * or a `spatial_goal` (a region boundary).
 */
export function hasBodyRegionCoverage(annotations: readonly unknown[]): boolean {
  return annotations.some((annotation) => {
    const record = asRecord(annotation);
    if (!record) return false;
    return (
      truthyAsPython(record.landmark_indices) || truthyAsPython(record.spatial_goal)
    );
  });
}

export interface CheckpointsCarryNoWeightInput {
  /** `metadata.global_body_match_enabled` — the panel's Body Form toggle. */
  bodyFormEnabled: boolean;
  /** `metadata.global_body_match_weight` — the Scoring Emphasis slider, 0–1. */
  bodyFormWeight: number;
  /** The template's annotations, as the studio currently holds them. */
  annotations: readonly unknown[];
}

/** All four clauses. See this module's header for why each one is required. */
export function checkpointsCarryNoWeight({
  bodyFormEnabled,
  bodyFormWeight,
  annotations,
}: CheckpointsCarryNoWeightInput): boolean {
  if (!bodyFormEnabled) return false;
  if (!Number.isFinite(bodyFormWeight) || bodyFormWeight < 1) return false;
  if (countCheckpoints(annotations) === 0) return false;
  return hasBodyRegionCoverage(annotations);
}

/**
 * The quiet inline line shown beside the slider.
 *
 * One short line, what / why / how, in the panel's own labels ("Body Form",
 * "My Goals") so the instruction points at a control the coach can see —
 * `docs/COACH_FACING_COPY_STANDARD.md` §1/§2. The finalize report carries the
 * longer three-sentence version; this one sits next to the cause.
 */
export const CHECKPOINTS_CARRY_NO_WEIGHT_LINE =
  "Your checkpoints don't change scores at Body Form 100% — move the slider toward My Goals to let them count.";
